import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Order, User } from '@app/core/api-client/models';
import { CartProduct } from '@app/core/services/cart/models/cart.model';
import { MapperService } from '@app/core/services/mapper.service';
import { OrderService } from '@app/core/services/order/order.service';
import { PageVisibilityService } from '@app/core/ui/page-visibility/page-visibility.service';
import { filter, interval, map, merge, Subscription, takeUntil, takeWhile, tap, throttleTime } from 'rxjs';
@Component({
    selector: 'app-command-detail',
    templateUrl: './command-detail.component.html',
    styleUrls: ['./command-detail.component.scss'],
    standalone: false
})
export class CommandDetailComponent implements OnChanges {
  private readonly _destroyRef = inject(DestroyRef);
  @Input() order: Order;
  @Output() exit = new EventEmitter();
  @Output() trackOrder = new EventEmitter<Order>();

  cartProductList: CartProduct[];

  private _monitorOrderPreparationSub: Subscription | null = null;

  constructor(
    private _mapperService: MapperService,
    private _orderService: OrderService,
    private _pageVisibilityService: PageVisibilityService,
    private _cd: ChangeDetectorRef,

  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    if (changes['order']) {
      this.cartProductList = this.order.products.map(p => this._mapperService.orderProductToCartProduct(p));
      this.monitorOrderPreparation(this.order);
    }
  }

  private monitorOrderPreparation(order: Order) {
    // Clean previous subscription
    if (this._monitorOrderPreparationSub) {
      this._monitorOrderPreparationSub.unsubscribe();
      this._monitorOrderPreparationSub = null;
    }

    // Monitor order preparation status if needed
    if (!this._orderService.orderPreparationShouldBeTracked(order)) {
      return;
    }

    // do it every 30s or when page becomes visible / app is resumed, if a timer (30s) is due
    this._monitorOrderPreparationSub = merge(
      interval(30000).pipe(
        map(() => 'Timer'),
        filter(() => this._pageVisibilityService.isPageVisible())
      ),
      this._pageVisibilityService.$onPageVisible.pipe(
        map(() => 'PageVisible'),
      )
    ).pipe(
      takeUntilDestroyed(this._destroyRef),
      throttleTime(30_000), // 30s
      tap((source) => console.log(`CommandDetailComponent: Refreshing orders triggered by ${source}`)),
      map((source) => this.order),
      takeWhile((order) => this._orderService.orderPreparationShouldBeTracked(order)),
      tap((order) => {
        this.refreshOrder(order.orderId);
      })
    )
      .subscribe();
  }

  private refreshOrder(orderId: number) {
    this._orderService.getOrder(orderId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((order) => {
          this.order = order;

          //TODO emit a sound when order is ready

          this._cd.markForCheck();
        })
      )
      .subscribe();
  }

  onExit() {
    this.exit.emit();
  }

  goToTrackOrder() {
    this.trackOrder.emit(this.order);
  }
}
